import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  ArrowLeft, BarChart3, DollarSign, TrendingUp, AlertTriangle,
  Download, ChevronDown, ChevronUp, Phone, MessageCircle,
  FileText, Users, Truck, CreditCard, Calendar
} from 'lucide-react';
import api from '../lib/api';

const fmt = (n) => `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Reportes = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('ventas');
  const [loading, setLoading] = useState(false);

  // Ventas state
  const [desde, setDesde] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  });
  const [hasta, setHasta] = useState(() => new Date().toISOString().slice(0, 10));
  const [ventas, setVentas] = useState(null);

  // Cobros state
  const [cobros, setCobros] = useState(null);
  const [expandedClient, setExpandedClient] = useState(null);

  useEffect(() => { fetchVentas(); }, []);

  const fetchVentas = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/reports/ventas?desde=${desde}&hasta=${hasta}`);
      setVentas(res.data);
    } catch { toast.error('Error cargando reporte'); }
    setLoading(false);
  };

  const fetchCobros = async () => {
    setLoading(true);
    try {
      const res = await api.get('/reports/cobros-pendientes');
      setCobros(res.data);
    } catch { toast.error('Error cargando cobros'); }
    setLoading(false);
  };

  const switchTab = (t) => {
    setTab(t);
    if (t === 'cobros' && !cobros) fetchCobros();
  };

  const downloadExcel = async () => {
    try {
      const res = await api.get(`/reports/ventas/excel?desde=${desde}&hasta=${hasta}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `ventas_${desde}_a_${hasta}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Excel descargado');
    } catch { toast.error('Error descargando Excel'); }
  };

  const maxVenta = ventas?.por_dia?.length ? Math.max(...ventas.por_dia.map(d => d.ventas), 1) : 1;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/')} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2" data-testid="reportes-title">
                <BarChart3 className="h-5 w-5 text-blue-600" /> Reportes Avanzados
              </h1>
            </div>
          </div>
          <Button onClick={downloadExcel} variant="outline" size="sm" className="gap-2" data-testid="download-excel-btn">
            <Download className="h-4 w-4" /> Excel
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-5">
        {/* Tabs */}
        <div className="flex gap-2 mb-5">
          <button onClick={() => switchTab('ventas')} data-testid="tab-ventas"
            className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all ${tab === 'ventas' ? 'bg-blue-600 text-white shadow-md' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}>
            <TrendingUp className="h-4 w-4" /> Ventas por Periodo
          </button>
          <button onClick={() => switchTab('cobros')} data-testid="tab-cobros"
            className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all ${tab === 'cobros' ? 'bg-red-600 text-white shadow-md' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}>
            <AlertTriangle className="h-4 w-4" /> Cobros Pendientes
          </button>
        </div>

        {/* ===== VENTAS TAB ===== */}
        {tab === 'ventas' && (
          <div className="space-y-5">
            {/* Date filters */}
            <div className="bg-white rounded-2xl border border-gray-200 p-4 flex flex-wrap items-end gap-3">
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Desde</label>
                <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)}
                  className="h-10 mt-1 w-40" data-testid="ventas-desde" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Hasta</label>
                <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)}
                  className="h-10 mt-1 w-40" data-testid="ventas-hasta" />
              </div>
              <Button onClick={fetchVentas} disabled={loading} className="h-10 bg-blue-600 hover:bg-blue-700" data-testid="ventas-buscar">
                {loading ? 'Cargando...' : 'Consultar'}
              </Button>
            </div>

            {ventas && (
              <>
                {/* KPI Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="ventas-kpis">
                  <KpiCard label="Envios" value={ventas.resumen.total_envios} icon={FileText} color="blue" isMoney={false} />
                  <KpiCard label="Ventas" value={ventas.resumen.total_ventas} icon={DollarSign} color="green" />
                  <KpiCard label="Cobrado" value={ventas.resumen.total_cobrado} icon={CreditCard} color="emerald" />
                  <KpiCard label="Pendiente" value={ventas.resumen.total_pendiente} icon={AlertTriangle} color="red" />
                </div>

                {/* Utilidad row */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <KpiCard label="Gastos" value={ventas.resumen.total_gastos} icon={TrendingUp} color="orange" />
                  <KpiCard label="Utilidad Neta" value={ventas.resumen.utilidad} icon={BarChart3} color={ventas.resumen.utilidad >= 0 ? 'green' : 'red'} />
                  <KpiCard label="Descuentos" value={ventas.resumen.total_descuento} icon={DollarSign} color="gray" />
                </div>

                {/* Daily chart */}
                {ventas.por_dia.length > 0 && (
                  <div className="bg-white rounded-2xl border border-gray-200 p-5">
                    <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-blue-500" /> Ventas por Dia
                    </h3>
                    <div className="space-y-2">
                      {ventas.por_dia.map((d, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <span className="text-xs text-gray-400 w-20 flex-shrink-0">{d.fecha.slice(5)}</span>
                          <div className="flex-1 bg-gray-100 rounded-full h-7 overflow-hidden relative">
                            <div className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all flex items-center"
                              style={{ width: `${Math.max((d.ventas / maxVenta) * 100, 2)}%` }}>
                            </div>
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-700">
                              {fmt(d.ventas)} ({d.envios})
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* By Furgon */}
                {ventas.por_furgon.length > 0 && (
                  <div className="bg-white rounded-2xl border border-gray-200 p-5">
                    <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                      <Truck className="h-4 w-4 text-indigo-500" /> Ventas por Furgon
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-100">
                            <th className="text-left py-2 text-gray-500 font-semibold text-xs uppercase">Furgon</th>
                            <th className="text-right py-2 text-gray-500 font-semibold text-xs uppercase">Envios</th>
                            <th className="text-right py-2 text-gray-500 font-semibold text-xs uppercase">Ventas</th>
                            <th className="text-right py-2 text-gray-500 font-semibold text-xs uppercase">Cobrado</th>
                            <th className="text-right py-2 text-gray-500 font-semibold text-xs uppercase">Pendiente</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ventas.por_furgon.map((f, i) => (
                            <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                              <td className="py-2.5 font-semibold text-gray-800">{f.furgon}</td>
                              <td className="py-2.5 text-right text-gray-600">{f.envios}</td>
                              <td className="py-2.5 text-right font-semibold text-gray-800">{fmt(f.ventas)}</td>
                              <td className="py-2.5 text-right text-green-600">{fmt(f.cobrado)}</td>
                              <td className="py-2.5 text-right text-red-600">{fmt(f.pendiente)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Top Customers */}
                {ventas.top_clientes.length > 0 && (
                  <div className="bg-white rounded-2xl border border-gray-200 p-5">
                    <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                      <Users className="h-4 w-4 text-green-500" /> Top Clientes del Periodo
                    </h3>
                    <div className="space-y-2">
                      {ventas.top_clientes.map((c, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-sm">
                              {i + 1}
                            </div>
                            <div>
                              <p className="font-semibold text-gray-800 text-sm">{c.nombre}</p>
                              <p className="text-xs text-gray-400">{c.telefono} &middot; {c.envios} envios</p>
                            </div>
                          </div>
                          <span className="font-bold text-gray-900">{fmt(c.total)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ===== COBROS TAB ===== */}
        {tab === 'cobros' && (
          <div className="space-y-5">
            {loading && !cobros && (
              <div className="text-center py-12 text-gray-400">Cargando cobros pendientes...</div>
            )}

            {cobros && (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="cobros-kpis">
                  <KpiCard label="Total Pendiente" value={cobros.resumen.total_pendiente} icon={DollarSign} color="red" />
                  <KpiCard label="Facturas Pendientes" value={cobros.resumen.total_facturas} icon={FileText} color="orange" isMoney={false} />
                  <KpiCard label="Clientes con Deuda" value={cobros.resumen.total_clientes} icon={Users} color="yellow" isMoney={false} />
                </div>

                {/* Client list */}
                <div className="space-y-3" data-testid="cobros-list">
                  {cobros.clientes.map((c, ci) => (
                    <div key={ci} className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                      {/* Client header */}
                      <button
                        onClick={() => setExpandedClient(expandedClient === ci ? null : ci)}
                        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                        data-testid={`cobro-client-${ci}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                            <DollarSign className="h-5 w-5 text-red-500" />
                          </div>
                          <div className="text-left">
                            <p className="font-bold text-gray-900">{c.nombre}</p>
                            <p className="text-xs text-gray-400">{c.telefono} &middot; {c.cantidad_facturas} factura{c.cantidad_facturas > 1 ? 's' : ''}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-lg font-bold text-red-600">{fmt(c.total_pendiente)}</span>
                          {expandedClient === ci ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
                        </div>
                      </button>

                      {/* Expanded invoices */}
                      {expandedClient === ci && (
                        <div className="border-t border-gray-100 bg-gray-50/50 p-4">
                          <div className="flex gap-2 mb-3">
                            {c.telefono && (
                              <a href={`tel:${c.telefono}`} className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-600 px-3 py-1.5 rounded-full font-semibold hover:bg-blue-100 transition-colors">
                                <Phone className="h-3 w-3" /> Llamar
                              </a>
                            )}
                            {c.telefono && (
                              <a href={`https://wa.me/${c.telefono.replace(/\D/g, '')}?text=${encodeURIComponent(`Hola ${c.nombre}! Le recordamos que tiene un saldo pendiente de ${fmt(c.total_pendiente)} en DOMINICAN SHIPPING.`)}`}
                                target="_blank" rel="noreferrer"
                                className="inline-flex items-center gap-1 text-xs bg-green-50 text-green-600 px-3 py-1.5 rounded-full font-semibold hover:bg-green-100 transition-colors">
                                <MessageCircle className="h-3 w-3" /> WhatsApp
                              </a>
                            )}
                          </div>
                          <div className="space-y-2">
                            {c.facturas.map((f, fi) => (
                              <div key={fi} className="bg-white rounded-xl p-3 border border-gray-100 flex flex-wrap items-center gap-x-4 gap-y-1">
                                <span className="font-bold text-gray-800 text-sm">{f.numero_factura}</span>
                                {f.numero_furgon && <span className="text-xs bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded font-medium">F-{f.numero_furgon}</span>}
                                {f.dias_pendiente > 0 && (
                                  <span className={`text-xs px-2 py-0.5 rounded font-medium ${f.dias_pendiente > 7 ? 'bg-red-50 text-red-600' : f.dias_pendiente > 3 ? 'bg-orange-50 text-orange-600' : 'bg-yellow-50 text-yellow-600'}`}>
                                    {f.dias_pendiente}d
                                  </span>
                                )}
                                <div className="ml-auto flex items-center gap-3 text-xs">
                                  <span className="text-gray-400">Total: <span className="font-semibold text-gray-700">{fmt(f.total)}</span></span>
                                  <span className="text-green-600">Pagado: {fmt(f.monto_pagado)}</span>
                                  <span className="font-bold text-red-600">{fmt(f.monto_pendiente)}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}

                  {cobros.clientes.length === 0 && (
                    <div className="text-center py-12 bg-white rounded-2xl border border-gray-200">
                      <DollarSign className="h-12 w-12 text-green-300 mx-auto mb-3" />
                      <p className="text-gray-500 font-semibold">No hay cobros pendientes</p>
                      <p className="text-gray-400 text-sm">Todos los clientes estan al dia</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const KpiCard = ({ label, value, icon: Icon, color, isMoney = true }) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    red: 'bg-red-50 text-red-600',
    orange: 'bg-orange-50 text-orange-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    gray: 'bg-gray-50 text-gray-600',
  };
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colors[color] || colors.blue}`}>
          <Icon className="h-4 w-4" />
        </div>
        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{label}</span>
      </div>
      <p className="text-xl font-bold text-gray-900">{isMoney ? fmt(value) : value}</p>
    </div>
  );
};

export default Reportes;
